"use client";

import { useEffect, useState } from "react";
import { Button } from "@/components/Button";
import { ErrorCard } from "@/components/ErrorCard";
import { recoverFromChunkError } from "@/lib/chunkRecovery";
import { reportClientError } from "@/lib/telemetry";

export default function AuthError({
  error,
  unstable_retry,
}: {
  error: Error & { digest?: string };
  unstable_retry: () => void;
}) {
  // A chunk that vanished under a mid-visit deploy is repairable, and the
  // card below can't repair it — reload instead. Decided during the first
  // render (via the lazy initializer) so the crash screen never flashes up
  // on its way out; the sessionStorage guard inside makes the repeat call
  // under StrictMode's double render a no-op.
  const [recovering] = useState(() => recoverFromChunkError(error));

  useEffect(() => {
    console.error(error);
    reportClientError(error, "auth", { recovered: String(recovering) });
  }, [error, recovering]);

  // The reload is already scheduled; showing anything would only flicker.
  if (recovering) return null;

  return (
    <div className="mx-auto max-w-[480px] px-[var(--gutter)] py-16">
      <ErrorCard
        title="Sign-in hit a snag"
        body="Something went wrong on our side. Try again — your account is unaffected."
        digest={error.digest}
        action={
          <>
            <Button onClick={() => unstable_retry()}>Try again</Button>
            <Button variant="secondary" href="/">
              Back home
            </Button>
          </>
        }
      />
    </div>
  );
}
